import axios from 'axios';
import { amplifyAuthService } from './amplifyAuth';
import {
  LogHabitRequest,
  LogHabitResponse,
  ReportRequest,
  ReportResponse,
  DefinitionsResponse,
  HabitDefinitionResponse,
  HabitVersionsResponse,
  UpdateHabitDescriptionRequest,
  UpdateHabitDescriptionResponse,
  UpdateHabitCategoryRequest,
  UpdateHabitCategoryResponse,
  UpdateHabitNameRequest,
  UpdateHabitNameResponse,
  UpdateHabitGoalRequest,
  UpdateHabitGoalResponse,
  CategoriesResponse,
  CreateHabitRequest,
  CreateHabitResponse,
  DeleteHabitResponse,
  CreateNewVersionResponse,
  PublishHabitVersionRequest,
  PublishHabitVersionResponse,
  QuoteResponse,
  RamResponse,
  UpdateRamRequest,
  MapGistResponse,
  UpdateMapGistRequest,
  FileSystemTreeResponse,
  FileSystemResponse,
  CreateFileRequest,
  CreateFolderRequest,
  UpdateFileRequest,
  UpdateFolderRequest,
  MoveNodeRequest,
  ChecklistResponse,
  UserChecklistLookupResponse,
  ItemResponse,
  CreateItemRequest,
  UpdateItemRequest,
  MarkDoneRequest,
  ReorderItemRequest,
  MoveItemRequest,
  GetAllDatedChecklistsResponse,
  CreateDatedChecklistRequest,
  DatedChecklistResponse,
  RecurringItemsListResponse,
  RecurringItemResponse,
  CreateRecurringItemRequest,
  UpdateRecurringItemRequest,
  GenerateRecurringItemsResponse,
  BlockSequencesResponse,
  BlockSequenceHeadBlockResponse,
  UpdateBlockTextRequest,
  UpdateBlockTextResponse,
  CreateBlockRequest,
  CreateBlockResponse,
  DeleteBlockResponse,
  CompleteBlockResponse,
  BlockWallResponse,
  WallRowInput,
  SaveBlockWallRequest,
  SaveBlockWallResponse,
  BlockNodeGraphResponse,
} from '../types/api';

export const resolveApiBaseUrl = (): string => process.env.REACT_APP_API_URL || '/api';

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(async (config) => {
  const accessToken = await amplifyAuthService.getAccessToken();
  if (!accessToken) {
    return config;
  }

  config.headers = config.headers ?? {};
  config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

export const habitService = {
  // Log a habit
  async logHabit(request: LogHabitRequest): Promise<LogHabitResponse> {
    try {
      const response = await apiClient.post<LogHabitResponse>('/habits/log', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get habit report
  async getReport(request: ReportRequest = {}): Promise<ReportResponse> {
    try {
      const params = new URLSearchParams();
      if (request.weeks) params.append('weeks', request.weeks.toString());
      if (request.category) params.append('category', request.category);
      if (request.type) params.append('type', request.type);
      if (request.sortBy) params.append('sortBy', request.sortBy);
      if (request.includeChildren) params.append('includeChildren', 'true');

      const response = await apiClient.get<ReportResponse>(`/habits/report?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get habit definitions
  async getDefinitions(): Promise<DefinitionsResponse> {
    try {
      const response = await apiClient.get<DefinitionsResponse>('/habits/definitions');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
      };
    }
  },

  // Get a single habit definition with fresh data
  async getHabit(habitId: string): Promise<HabitDefinitionResponse> {
    try {
      const response = await apiClient.get<HabitDefinitionResponse>(`/habits/${habitId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get all versions of a habit
  async getHabitVersions(habitId: string): Promise<HabitVersionsResponse> {
    try {
      const response = await apiClient.get<HabitVersionsResponse>(`/habits/${habitId}/versions`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update habit description with optimistic locking
  async updateDescription(
    habitId: string, 
    request: UpdateHabitDescriptionRequest
  ): Promise<UpdateHabitDescriptionResponse> {
    try {
      const response = await apiClient.patch<UpdateHabitDescriptionResponse>(
        `/habits/${habitId}/description`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update habit category
  async updateCategory(
    habitId: string,
    request: UpdateHabitCategoryRequest
  ): Promise<UpdateHabitCategoryResponse> {
    try {
      const response = await apiClient.patch<UpdateHabitCategoryResponse>(
        `/habits/${habitId}/category`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update habit name
  async updateName(
    habitId: string,
    request: UpdateHabitNameRequest
  ): Promise<UpdateHabitNameResponse> {
    try {
      const response = await apiClient.patch<UpdateHabitNameResponse>(
        `/habits/${habitId}/name`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update habit goal
  async updateGoal(
    habitId: string,
    request: UpdateHabitGoalRequest
  ): Promise<UpdateHabitGoalResponse> {
    try {
      const response = await apiClient.patch<UpdateHabitGoalResponse>(
        `/habits/${habitId}/goal`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get habit categories
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await apiClient.get<CategoriesResponse>('/habits/categories');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
      };
    }
  },

  // Create a new habit
  async createHabit(request: CreateHabitRequest): Promise<CreateHabitResponse> {
    try {
      const response = await apiClient.post<CreateHabitResponse>('/habits', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete a habit and all its data
  async deleteHabit(habitId: string): Promise<DeleteHabitResponse> {
    try {
      const response = await apiClient.delete<DeleteHabitResponse>(`/habits/${habitId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new draft version of a habit by copying the latest published version
  async createNewVersion(habitId: string): Promise<CreateNewVersionResponse> {
    try {
      const response = await apiClient.post<CreateNewVersionResponse>(`/habits/${habitId}/versions`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Publish a draft habit version
  async publishHabitVersion(habitId: string, request: PublishHabitVersionRequest): Promise<PublishHabitVersionResponse> {
    try {
      const response = await apiClient.post<PublishHabitVersionResponse>(`/habits/${habitId}/publish`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Health check
  async healthCheck(): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      throw new Error('API server is not accessible');
    }
  },
};

// Quote service with local caching
export const quoteService = {
  // Cache for quote of the day
  cachedQuote: null as QuoteResponse['data'] | null,
  cacheDate: null as string | null,

  // Get quote of the day with caching
  async getQuoteOfTheDay(): Promise<QuoteResponse> {
    try {
      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

      // Check if we have a valid cached quote for today
      if (this.cachedQuote && this.cacheDate === today) {
        return {
          success: true,
          data: this.cachedQuote,
        };
      }

      // Fetch new quote from backend
      const response = await apiClient.get<QuoteResponse>(`/quote-of-the-day?timezone=${encodeURIComponent(timezone)}`);
      
      if (response.data.success && response.data.data) {
        // Update cache
        this.cachedQuote = response.data.data;
        this.cacheDate = today;
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Clear cache (useful for testing or manual refresh)
  clearCache() {
    this.cachedQuote = null;
    this.cacheDate = null;
  },
};

export const ramService = {
  async getRam(): Promise<RamResponse> {
    try {
      const response = await apiClient.get<RamResponse>('/ram');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async updateRam(request: UpdateRamRequest): Promise<RamResponse> {
    try {
      const response = await apiClient.put<RamResponse>('/ram', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

export const mapGistService = {
  async getMapGist(): Promise<MapGistResponse> {
    try {
      const response = await apiClient.get<MapGistResponse>('/map-gist');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async updateMapGist(request: UpdateMapGistRequest): Promise<MapGistResponse> {
    try {
      const response = await apiClient.put<MapGistResponse>('/map-gist', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

// File system service
export const fileSystemService = {
  // Get entire file system structure
  async getFileSystem(): Promise<FileSystemTreeResponse> {
    try {
      const response = await apiClient.get<FileSystemTreeResponse>('/filesystem');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get a specific file
  async getFile(id: string): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.get<FileSystemResponse>(`/files/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new file
  async createFile(request: CreateFileRequest): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.post<FileSystemResponse>('/files', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Upload a file
  async uploadFile(request: { parentId: string; file: File }): Promise<FileSystemResponse> {
    try {
      const formData = new FormData();
      formData.append('file', request.file);
      if (request.parentId) {
        formData.append('parentId', request.parentId);
      }

      const response = await apiClient.post<FileSystemResponse>('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update a file
  async updateFile(id: string, request: UpdateFileRequest): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.put<FileSystemResponse>(`/files/${id}`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete a file
  async deleteFile(id: string): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.delete<FileSystemResponse>(`/files/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get a specific folder
  async getFolder(id: string): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.get<FileSystemResponse>(`/folders/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new folder
  async createFolder(request: CreateFolderRequest): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.post<FileSystemResponse>('/folders', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update a folder
  async updateFolder(id: string, request: UpdateFolderRequest): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.put<FileSystemResponse>(`/folders/${id}`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete a folder
  async deleteFolder(id: string): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.delete<FileSystemResponse>(`/folders/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Move a file or folder
  async moveNode(id: string, request: MoveNodeRequest): Promise<FileSystemResponse> {
    try {
      const response = await apiClient.post<FileSystemResponse>(`/files/${id}/move`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

// Checklist service
export const checklistService = {
  // Get the user's checklist lookup rows keyed by KSUID.
  async getUserChecklists(): Promise<UserChecklistLookupResponse> {
    try {
      const response = await apiClient.get<UserChecklistLookupResponse>('/checklists');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get checklist items
  async getChecklist(checklistIdentifier: string): Promise<ChecklistResponse> {
    try {
      const response = await apiClient.get<ChecklistResponse>(`/checklists/${checklistIdentifier}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new item
  async createItem(checklistIdentifier: string, request: CreateItemRequest): Promise<ItemResponse> {
    try {
      const response = await apiClient.post<ItemResponse>(`/checklists/${checklistIdentifier}/items`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update an item's text
  async updateItem(checklistIdentifier: string, itemId: string, request: UpdateItemRequest): Promise<ItemResponse> {
    try {
      const response = await apiClient.put<ItemResponse>(`/checklists/${checklistIdentifier}/items/${itemId}`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Mark an item as done or undone
  async markItemDone(checklistIdentifier: string, itemId: string, request: MarkDoneRequest): Promise<ItemResponse> {
    try {
      const response = await apiClient.patch<ItemResponse>(`/checklists/${checklistIdentifier}/items/${itemId}/done`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete an item
  async deleteItem(checklistIdentifier: string, itemId: string): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.delete(`/checklists/${checklistIdentifier}/items/${itemId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Reorder an item within the same checklist
  async reorderItem(checklistIdentifier: string, itemId: string, request: ReorderItemRequest): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.post(`/checklists/${checklistIdentifier}/items/${itemId}/reorder`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Move an item to a different checklist
  async moveItem(sourceChecklistIdentifier: string, itemId: string, request: MoveItemRequest): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.post(`/checklists/${sourceChecklistIdentifier}/items/${itemId}/move`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update an item's custom color
  async updateItemColor(checklistIdentifier: string, itemId: string, customColor: string): Promise<ItemResponse> {
    try {
      const response = await apiClient.patch<ItemResponse>(`/checklists/${checklistIdentifier}/items/${itemId}/color`, { customColor });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

// Dated checklist service
export const datedChecklistService = {
  // Get all dated checklists
  async getAllChecklists(): Promise<{ success: boolean; data?: GetAllDatedChecklistsResponse; error?: string; code?: string }> {
    try {
      const response = await apiClient.get('/dated-checklists');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get a specific dated checklist
  async getChecklist(date: string): Promise<DatedChecklistResponse> {
    try {
      const response = await apiClient.get<DatedChecklistResponse>(`/dated-checklists/${date}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new dated checklist
  async createChecklist(request: CreateDatedChecklistRequest): Promise<DatedChecklistResponse> {
    try {
      const response = await apiClient.post<DatedChecklistResponse>('/dated-checklists', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete a dated checklist
  async deleteChecklist(date: string): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.delete(`/dated-checklists/${date}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create an item in a dated checklist
  async createItem(date: string, request: CreateItemRequest): Promise<ItemResponse> {
    try {
      const response = await apiClient.post<ItemResponse>(`/dated-checklists/${date}/items`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update an item in a dated checklist
  async updateItem(date: string, itemId: string, request: UpdateItemRequest): Promise<ItemResponse> {
    try {
      const response = await apiClient.put<ItemResponse>(`/dated-checklists/${date}/items/${itemId}`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Make an existing item recurring (atomic operation)
  async makeItemRecurring(date: string, itemId: string, request: {
    text: string;
    recurrenceType: string;
    weekdays?: number[];
    monthDays?: number[];
    customColor?: string;
  }): Promise<ItemResponse> {
    try {
      const response = await apiClient.post<ItemResponse>(`/dated-checklists/${date}/items/${itemId}/make-recurring`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Convert an existing item to recurring
  async convertItemToRecurring(date: string, itemId: string, recurringMetadataId: string): Promise<ItemResponse> {
    try {
      const response = await apiClient.post<ItemResponse>(`/dated-checklists/${date}/items/${itemId}/convert-to-recurring`, {
        recurringMetadataId,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Convert an existing recurring item to non-recurring
  async convertItemToNonRecurring(date: string, itemId: string, text?: string): Promise<ItemResponse> {
    try {
      const response = await apiClient.post<ItemResponse>(`/dated-checklists/${date}/items/${itemId}/convert-to-non-recurring`, {
        text: text || '',
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Mark an item as done
  async markItemDone(date: string, itemId: string, request: MarkDoneRequest): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.patch(`/dated-checklists/${date}/items/${itemId}/done`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete an item from a dated checklist
  async deleteItem(date: string, itemId: string): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.delete(`/dated-checklists/${date}/items/${itemId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Reorder an item within a dated checklist
  async reorderItem(date: string, itemId: string, request: ReorderItemRequest): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.post(`/dated-checklists/${date}/items/${itemId}/reorder`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update an item's custom color
  async updateItemColor(date: string, itemId: string, customColor: string): Promise<ItemResponse> {
    try {
      const response = await apiClient.patch<ItemResponse>(`/dated-checklists/${date}/items/${itemId}/color`, { customColor });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Mark a dated checklist as reviewed or unreviewed
  async markReviewed(date: string, reviewed: boolean): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.patch(`/dated-checklists/${date}/reviewed`, { reviewed });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get the count of items in a dated checklist without retrieving the items
  async getChecklistItemCount(date: string): Promise<{ success: boolean; count?: number; error?: string; code?: string }> {
    try {
      const response = await apiClient.get<{ success: boolean; count: number }>(`/dated-checklists/${date}/count`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

// Recurring items service
export const recurringItemService = {
  // Get all recurring items
  async getAllRecurringItems(): Promise<RecurringItemsListResponse> {
    try {
      const response = await apiClient.get<RecurringItemsListResponse>('/recurring-items');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        count: 0,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get a specific recurring item
  async getRecurringItem(id: string): Promise<RecurringItemResponse> {
    try {
      const response = await apiClient.get<RecurringItemResponse>(`/recurring-items/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Create a new recurring item
  async createRecurringItem(request: CreateRecurringItemRequest): Promise<RecurringItemResponse> {
    try {
      const response = await apiClient.post<RecurringItemResponse>('/recurring-items', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Update an existing recurring item
  async updateRecurringItem(id: string, request: UpdateRecurringItemRequest): Promise<RecurringItemResponse> {
    try {
      const response = await apiClient.put<RecurringItemResponse>(`/recurring-items/${id}`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete a recurring item
  // DEPRECATED: Do not use - deleting recurring items is not supported
  // Use convertItemToNonRecurring instead to keep one instance and delete the pattern
  async deleteRecurringItem(id: string): Promise<{ success: boolean; error?: string; code?: string }> {
    console.warn('[DEPRECATED] deleteRecurringItem is deprecated and will be removed. Use convertItemToNonRecurring instead.');
    try {
      const response = await apiClient.delete(`/recurring-items/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Deleting recurring items is not supported. Use Convert to Non-Recurring instead.',
        code: 'DELETE_RECURRING_ITEM_NOT_SUPPORTED',
      };
    }
  },

  // Generate recurring items for due/overdue dates
  async generateRecurringItems(timezone?: string): Promise<GenerateRecurringItemsResponse> {
    try {
      const response = await apiClient.post<GenerateRecurringItemsResponse>('/recurring-items/generate', {
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        generated: 0,
        datesCreated: [],
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Delete generated copies of a recurring item (when converting to non-recurring)
  async deleteGeneratedCopies(id: string, text: string, keepOnDate: string, keepItemId: string): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.post(`/recurring-items/${id}/delete-copies`, {
        text,
        keepOnDate,
        keepItemId,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Get recurring items for a specific date
  async getRecurringItemsForDate(date: string): Promise<RecurringItemsListResponse> {
    try {
      const response = await apiClient.get<RecurringItemsListResponse>(`/recurring-items/for-date/${date}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        count: 0,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  // Sync a recurring item to all existing dates
  async syncRecurringItemToExistingDates(id: string, oldWeekdays?: number[]): Promise<{ success: boolean; error?: string; code?: string }> {
    try {
      const response = await apiClient.post(`/recurring-items/${id}/sync`, {
        oldWeekdays,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

export const blockService = {
  async getAllBlockSequences(): Promise<BlockSequencesResponse> {
    try {
      const response = await apiClient.get<BlockSequencesResponse>('/block-sequences');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async getBlockSequenceHeadBlock(blockSequenceId: string): Promise<BlockSequenceHeadBlockResponse> {
    try {
      const response = await apiClient.get<BlockSequenceHeadBlockResponse>(
        `/block-sequences/${blockSequenceId}/head-block`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return { success: false, error: 'Network error occurred', code: 'NETWORK_ERROR' };
    }
  },

  async updateBlockText(
    blockSequenceId: string,
    blockId: string,
    text: string
  ): Promise<UpdateBlockTextResponse> {
    try {
      const request: UpdateBlockTextRequest = { text };
      const response = await apiClient.patch<UpdateBlockTextResponse>(
        `/block-sequences/${blockSequenceId}/blocks/${blockId}`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async createBlock(
    blockSequenceId: string,
    predecessorBlockId: string
  ): Promise<CreateBlockResponse> {
    try {
      const request: CreateBlockRequest = { predecessorBlockId };
      const response = await apiClient.post<CreateBlockResponse>(
        `/block-sequences/${blockSequenceId}/blocks`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async deleteBlock(
    blockSequenceId: string,
    blockId: string
  ): Promise<DeleteBlockResponse> {
    try {
      const response = await apiClient.delete<DeleteBlockResponse>(
        `/block-sequences/${blockSequenceId}/blocks/${blockId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },

  async getBlockWall(): Promise<BlockWallResponse> {
    try {
      const response = await apiClient.get<BlockWallResponse>('/block-wall');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return { success: false, rows: [], error: 'Network error occurred', code: 'NETWORK_ERROR' };
    }
  },

  async getBlockNodeGraph(habitName?: string): Promise<BlockNodeGraphResponse> {
    try {
      const response = await apiClient.get<BlockNodeGraphResponse>('/block-graph', {
        params: habitName ? { habit: habitName } : undefined,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return { success: false, error: 'Network error occurred', code: 'NETWORK_ERROR' };
    }
  },

  async saveBlockWall(rows: WallRowInput[]): Promise<SaveBlockWallResponse> {
    try {
      const request: SaveBlockWallRequest = { rows };
      const response = await apiClient.put<SaveBlockWallResponse>('/block-wall', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return { success: false, rows: [], error: 'Network error occurred', code: 'NETWORK_ERROR' };
    }
  },

  async completeBlock(
    blockSequenceId: string,
    blockId: string
  ): Promise<CompleteBlockResponse> {
    try {
      const response = await apiClient.post<CompleteBlockResponse>(
        `/block-sequences/${blockSequenceId}/blocks/${blockId}/complete`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Network error occurred',
        code: 'NETWORK_ERROR',
      };
    }
  },
};

// Get habit display name from definitions or fall back to formatted key
export const getHabitDisplayName = (
  habitKey: string,
  definitions?: Record<string, { habitDisplayName: string }>
): string => {
  if (definitions && definitions[habitKey]?.habitDisplayName) {
    return definitions[habitKey].habitDisplayName;
  }
  
  // Fallback: format the key if definitions not available
  return habitKey
    .replace(/_/g, ' ')
    .replace(/\//g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};